import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Loader2, Shield, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiGetAccessRequest, apiApproveAccessRequest, apiRejectAccessRequest } from '@/services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApproveAccess() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const actionParam = searchParams.get('action'); // 'reject' from email link

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSedes, setSelectedSedes] = useState([]);
  const [allSedes, setAllSedes] = useState([]);
  const [sedeInput, setSedeInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [alreadyHandled, setAlreadyHandled] = useState(null); // 'approved' | 'rejected'
  const [filtered, setFiltered] = useState([]);

  // Load sedes dynamically from ArcGIS
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/filters/sedes`)
      .then(r => r.json())
      .then(data => {
        // API returns [{value:"CODE - NAME", label, code}]; normalize to "CODE - NAME" strings for display
        const strings = Array.isArray(data)
          ? data.map(item => (typeof item === 'object' && item.value ? item.value : String(item)))
          : [];
        setAllSedes(strings);
        setFiltered(strings.slice(0, 20));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!requestId || !token) {
      setError('Enlace de aprobación inválido o incompleto.');
      setLoading(false);
      return;
    }
    apiGetAccessRequest(requestId, token)
      .then(data => {
        setRequest(data);
        if (data.status === 'approved') {
          setAlreadyHandled('approved');
          setSelectedSedes(data.assigned_sedes || []);
        } else if (data.status === 'rejected') {
          setAlreadyHandled('rejected');
        } else if (data.requested_sedes?.length > 0) {
          setSelectedSedes(data.requested_sedes.map(s => s.split(' - ')[0].trim()));
        }
        setLoading(false);

        // Auto-trigger rejection if action=reject from email link
        if (actionParam === 'reject' && data.status === 'pending') {
          handleRejectDirect(requestId, token);
        }
      })
      .catch(err => {
        setError(err?.response?.data?.detail || 'Token expirado o inválido. Solicita un nuevo enlace.');
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, token, actionParam]);

  useEffect(() => {
    const q = sedeInput.trim().toLowerCase();
    const base = allSedes.length > 0 ? allSedes : [];
    if (!q) {
      setFiltered(base.filter(s => !selectedSedes.includes(s.split(' - ')[0].trim())).slice(0, 20));
    } else {
      setFiltered(base.filter(s => s.toLowerCase().includes(q) && !selectedSedes.includes(s.split(' - ')[0].trim())).slice(0, 30));
    }
  }, [sedeInput, selectedSedes, allSedes]);

  const handleRejectDirect = async (rid, tok) => {
    setSubmitting(true);
    try {
      await apiRejectAccessRequest(rid, tok);
      setRejected(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al procesar el rechazo.');
    } finally {
      setSubmitting(false);
    }
  };

  // Extract code from "CODE - NAME" format, or return as-is if no " - " pattern
  const extractCode = (s) => (s.includes(' - ') ? s.split(' - ')[0].trim() : s.trim());

  const toggleSede = (s) => {
    const code = extractCode(s);
    setSelectedSedes(prev =>
      prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]
    );
  };

  const handleApprove = async () => {
    if (selectedSedes.length === 0) {
      alert('Selecciona al menos una sede para asignar.');
      return;
    }
    setSubmitting(true);
    try {
      await apiApproveAccessRequest(requestId, selectedSedes, token);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al aprobar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await apiRejectAccessRequest(requestId, token);
      setRejected(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al rechazar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle = {
    background: '#111c11',
    border: '1px solid rgba(0,200,83,0.2)',
    borderRadius: 16,
    boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
    maxWidth: 600,
    width: '100%',
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: '#0a120a' }}
      data-testid="approve-access-page"
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={cardStyle} className="p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,200,83,0.15)' }}>
            <Shield size={20} style={{ color: '#00C853' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Panel de Aprobación</h1>
            <p className="text-xs" style={{ color: '#666' }}>Valoriza SafetyAI</p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: '#00C853' }} />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center py-8 gap-4 text-center" data-testid="approve-error">
            <AlertCircle size={44} style={{ color: '#e53935' }} />
            <div>
              <h2 className="text-lg font-bold text-white mb-1">
                {error.includes('no encontrada') ? 'Solicitud no encontrada' : 'Enlace inválido'}
              </h2>
              <p className="text-sm" style={{ color: '#aaa' }}>
                {error.includes('no encontrada')
                  ? 'Este enlace de aprobación ya no es válido. Puede que la solicitud haya sido gestionada o haya expirado.'
                  : error}
              </p>
            </div>
            <div className="rounded-lg p-4 w-full text-left text-sm" style={{ background: 'rgba(255,185,0,0.06)', border: '1px solid rgba(255,185,0,0.2)' }}>
              <p className="font-semibold mb-1.5" style={{ color: '#FFB900' }}>¿Qué puedes hacer?</p>
              <ul className="space-y-1" style={{ color: '#aaa' }}>
                <li>• Revisa si ya fue aprobada/rechazada en el Panel de Administración → Solicitudes</li>
                <li>• Si el usuario necesita acceso, pídele que inicie sesión y envíe una nueva solicitud</li>
                <li>• Los enlaces son válidos 7 días desde que se generó el email</li>
              </ul>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="mt-2 px-6"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              Ir al dashboard
            </Button>
          </div>
        )}

        {!loading && success && (
          <div className="flex flex-col items-center py-10 gap-4" data-testid="approve-success">
            <CheckCircle size={48} style={{ color: '#00C853' }} />
            <h2 className="text-xl font-bold text-white">¡Acceso aprobado!</h2>
            <p className="text-sm text-center" style={{ color: '#aaa' }}>
              Se ha notificado a <strong className="text-white">{request?.email}</strong>.<br />
              Puede iniciar sesión con su cuenta de Valoriza.
            </p>
            <div className="mt-4 p-3 rounded-lg w-full" style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#00C853' }}>Sedes asignadas ({selectedSedes.length})</p>
              <div className="flex flex-wrap gap-1">
                {selectedSedes.map(s => (
                  <Badge key={s} variant="outline" className="text-xs" style={{ borderColor: '#00C853', color: '#00C853' }}>{s}</Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && rejected && (
          <div className="flex flex-col items-center py-10 gap-4" data-testid="reject-success">
            <XCircle size={48} style={{ color: '#e53935' }} />
            <h2 className="text-xl font-bold text-white">Solicitud rechazada</h2>
            <p className="text-sm text-center" style={{ color: '#aaa' }}>
              Se ha notificado a <strong className="text-white">{request?.email}</strong> que su solicitud no ha sido aprobada.<br />
              El usuario puede volver a solicitarlo.
            </p>
          </div>
        )}

        {!loading && !error && !success && !rejected && request && (
          <>
            {alreadyHandled === 'approved' ? (
              <div className="flex flex-col items-center gap-3 py-6" data-testid="already-approved">
                <CheckCircle size={40} style={{ color: '#00C853' }} />
                <p className="text-white font-semibold">Esta solicitud ya fue aprobada</p>
                <p className="text-sm" style={{ color: '#aaa' }}>{request.email} ya tiene acceso configurado.</p>
              </div>
            ) : alreadyHandled === 'rejected' ? (
              <div className="flex flex-col items-center gap-3 py-6" data-testid="already-rejected">
                <XCircle size={40} style={{ color: '#e53935' }} />
                <p className="text-white font-semibold">Esta solicitud ya fue rechazada</p>
                <p className="text-sm" style={{ color: '#aaa' }}>{request.email} fue notificado.</p>
              </div>
            ) : (
              <>
                {/* Request summary */}
                <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#666' }}>Usuario</p>
                      <p className="text-white font-semibold">{request.name}</p>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#666' }}>Email</p>
                      <p className="text-white text-xs break-all">{request.email}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs mb-1" style={{ color: '#666' }}>Sedes solicitadas</p>
                      <p className="text-sm" style={{ color: '#ccc' }}>
                        {request.requested_sedes?.length > 0 ? request.requested_sedes.join(', ') : 'Sin especificar'}
                      </p>
                    </div>
                    {request.message && (
                      <div className="col-span-2">
                        <p className="text-xs mb-1" style={{ color: '#666' }}>Mensaje</p>
                        <p className="text-sm" style={{ color: '#ccc' }}>{request.message}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sede selector */}
                <div className="mb-5">
                  <label className="text-sm font-semibold mb-2 block text-white">
                    Asignar sedes ({selectedSedes.length} seleccionadas)
                  </label>
                  {selectedSedes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3 p-3 rounded-lg" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.15)' }}>
                      {selectedSedes.map(s => (
                        <button
                          key={s}
                          onClick={() => toggleSede(s)}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-md font-mono"
                          style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}
                          data-testid={`sede-badge-${s}`}
                        >
                          {s} <X size={10} />
                        </button>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={sedeInput}
                    onChange={e => setSedeInput(e.target.value)}
                    placeholder="Buscar código de sede..."
                    className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none mb-2"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="sede-search-input"
                  />
                  <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                    {filtered.map(s => (
                      <button
                        key={s}
                        onClick={() => toggleSede(s)}
                        className="text-xs px-2 py-1 rounded text-left truncate"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#aaa', border: '1px solid rgba(255,255,255,0.07)' }}
                        data-testid={`sede-option-${extractCode(s)}`}
                        title={s}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleApprove}
                    disabled={submitting || selectedSedes.length === 0}
                    className="flex-1 h-12 text-base font-bold gap-2"
                    style={{ background: '#00C853', color: '#000' }}
                    data-testid="approve-confirm-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="animate-spin" size={18} /> Procesando...</>
                    ) : (
                      <><CheckCircle size={18} /> Aprobar acceso</>
                    )}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={submitting}
                    variant="outline"
                    className="flex-1 h-12 text-base font-bold gap-2"
                    style={{ borderColor: '#e53935', color: '#e53935', background: 'transparent' }}
                    data-testid="reject-confirm-btn"
                  >
                    <XCircle size={18} /> Rechazar
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
